import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { ReactComponent as ExternalArrow } from '../../../../../assets/img/arrow-up-right.svg';
import { fontUi } from '../../../../../theme/typography';
import {
  liveScores,
  liveMinuteLabel,
  matchOpenPath,
} from '../../../../../service/liveMatch';
import { goToDiscordLogin } from '../../../../../service/functions';
import {
  sideMatchEvents,
  groupSideMatchEvents,
} from '../../../../../service/matchEngine';
import { MatchEventMark } from '../../../../../components/MatchMarks';
import {
  crestFromSources,
  teamInitials,
} from '../../../../../service/matchCrests';
import { calendarData } from '../../../../../redux/selector/League';
import { getInfoAboutDataHome } from '../../../../../redux/selector/Home';
import { getMyCurrentTeam } from '../../../../../redux/selector/User';
import { upcomingCalendarList } from '../../../../../redux/selector/Team';

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(0.85); }
`;

const Bar = styled.section`
  ${fontUi};
  flex: 1 0 100%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.cardRadius};
  color: ${({ theme }) => theme.text};
  box-shadow: 0 8px 20px rgba(0, 0, 0, ${({ theme }) => (theme.isDark ? 0.28 : 0.04)});

  .match {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  .top {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }

  .status {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.danger};
    padding: 5px 8px;
    border-radius: 999px;
    background: ${({ theme }) =>
      theme.isDark ? 'rgba(220, 53, 69, 0.16)' : 'rgba(220, 53, 69, 0.08)'};
  }

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: ${pulse} 1.4s ease-in-out infinite;
  }

  .minute {
    font-variant-numeric: tabular-nums;
    border-left: 1px solid
      ${({ theme }) =>
        theme.isDark ? 'rgba(220, 53, 69, 0.35)' : 'rgba(220, 53, 69, 0.22)'};
    padding-left: 6px;
    letter-spacing: 0;
    text-transform: none;
    font-size: 11px;
    font-weight: 700;
    color: ${({ theme }) => theme.textMuted};
  }

  .scoreboard {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-width: 0;
    flex: 1;
    border: 0;
    padding: 0;
    margin: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: inherit;
  }

  .scoreboard[data-open='true'] {
    cursor: pointer;
    border-radius: ${({ theme }) => theme.controlRadius};
  }

  .scoreboard[data-open='true']:hover .score strong {
    border-color: ${({ theme }) => theme.headerAccent || theme.border};
  }

  .scoreboard[data-open='true']:focus-visible {
    outline: 2px solid ${({ theme }) => theme.headerAccent};
    outline-offset: 3px;
  }

  .side {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    max-width: min(240px, 34%);
  }

  .side.home {
    justify-content: flex-end;
    text-align: right;
  }

  .side.away {
    justify-content: flex-start;
    text-align: left;
  }

  .name {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .crest,
  .crest-fallback {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }

  .crest {
    object-fit: contain;
    display: block;
  }

  .crest-fallback {
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: ${({ theme }) => theme.hover};
    border: 1px solid ${({ theme }) => theme.borderSubtle};
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: ${({ theme }) => theme.textMuted};
  }

  .score {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    font-size: 20px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  .score strong {
    display: grid;
    place-items: center;
    min-width: 30px;
    height: 34px;
    padding: 0 4px;
    box-sizing: border-box;
    background: ${({ theme }) => theme.hover};
    border: 1px solid ${({ theme }) => theme.borderSubtle};
    border-radius: ${({ theme }) => theme.controlRadius};
    font: inherit;
  }

  .separator {
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.textFaint};
  }

  .events {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 10px;
    min-width: 0;
  }

  .events-side {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 8px;
    min-width: 0;
    max-width: min(240px, 34%);
    flex: 1;
  }

  .events-side.home {
    justify-content: flex-end;
  }

  .events-side.away {
    justify-content: flex-start;
  }

  .events-gap {
    flex-shrink: 0;
    width: 78px;
  }

  .watch {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 13px;
    min-height: 36px;
    box-sizing: border-box;
    border: 1px solid #5865f2;
    border-radius: ${({ theme }) => theme.controlRadius};
    background: #5865f2;
    color: #fff;
    font: 600 12px Archivo, sans-serif;
    cursor: pointer;
  }

  .watch:hover {
    background: #4752c4;
    border-color: #4752c4;
  }

  .watch svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    color: inherit;
  }

  .watch:focus-visible {
    outline: 2px solid ${({ theme }) => theme.headerAccent};
    outline-offset: 3px;
  }

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px;

    .top {
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }

    .status {
      align-self: flex-start;
    }

    .scoreboard {
      width: 100%;
      gap: 8px;
    }

    .side {
      max-width: none;
      flex: 1;
    }

    .name {
      font-size: 12px;
    }

    .watch {
      width: 100%;
    }

    .events {
      flex-direction: column;
      gap: 8px;
    }

    .events-gap {
      display: none;
    }

    .events-side {
      max-width: none;
      flex: none;
      width: 100%;
    }

    .events-side.home {
      justify-content: flex-start;
    }
  }
`;

function TeamCrest({ src, name }) {
  if (src) {
    return <img className="crest" src={src} alt="" />;
  }
  return (
    <span className="crest-fallback" aria-hidden="true">
      {teamInitials(name)}
    </span>
  );
}

function EventsRow({ match, side, align }) {
  const events = groupSideMatchEvents(sideMatchEvents(match, side));
  if (!events.length) return <div className={`events-side ${align || ''}`} />;
  return (
    <div
      className={`events-side ${align || ''}`}
      aria-label={`${side} match events`}
    >
      {events.map((event) => (
        <MatchEventMark
          key={event.key}
          kind={event.kind}
          minute={event.minute}
          minutes={event.minutes}
          secondYellow={event.secondYellow}
          playerName={event.playerName}
          playerInName={event.playerInName}
          playerOutName={event.playerOutName}
        />
      ))}
    </div>
  );
}

function crestFromCalendar(calendar, teamId, teamName) {
  if (!Array.isArray(calendar)) return null;
  const hit = calendar.find(
    (event) =>
      (teamId && String(event?.team?._id) === String(teamId)) ||
      (teamName && event?.team?.name === teamName)
  );
  return hit?.team?.symbol || null;
}

export default function LiveMatchBar({
  match,
  discordChannel,
  isAuthenticated,
}) {
  const history = useHistory();
  const fixtures = useSelector(calendarData);
  const homeData = useSelector(getInfoAboutDataHome);
  const myTeam = useSelector(getMyCurrentTeam);
  const upcoming = useSelector(upcomingCalendarList);

  const score = liveScores(match);
  const minute = liveMinuteLabel(match);
  const openPath = matchOpenPath(match);
  const homeTeam = match?.home_team || {};
  const awayTeam = match?.away_team || {};
  const home = homeTeam.name || 'Home';
  const away = awayTeam.name || 'Away';

  const openMatch = () => {
    if (openPath) history.push(openPath);
  };

  const crestSources = useMemo(
    () => ({
      fixtures,
      ranking: Array.isArray(homeData?.rankingTable)
        ? homeData.rankingTable
        : null,
      homeSymbol: homeData?.symbol || myTeam?.symbol || null,
      homeTeamId: homeData?.teamId || myTeam?._id || null,
      homeTeamName: homeData?.teamName || myTeam?.name || null,
    }),
    [fixtures, homeData, myTeam]
  );

  const homeCrest =
    crestFromSources(homeTeam._id, home, {
      ...crestSources,
      matchTeam: homeTeam,
    }) || crestFromCalendar(upcoming, homeTeam._id, home);
  const awayCrest =
    crestFromSources(awayTeam._id, away, {
      ...crestSources,
      matchTeam: awayTeam,
    }) || crestFromCalendar(upcoming, awayTeam._id, away);

  const hasEvents =
    sideMatchEvents(match, 'home').length > 0 ||
    sideMatchEvents(match, 'away').length > 0;

  return (
    <Bar aria-label="Live match">
      <div className="match">
        <div className="top">
          <div className="status">
            <span className="dot" aria-hidden="true" />
            LIVE
            {minute && <span className="minute">{minute}</span>}
          </div>
          {score.hasScore && (
            <button
              type="button"
              className="scoreboard"
              data-open={openPath ? 'true' : 'false'}
              onClick={openMatch}
              disabled={!openPath}
              title={openPath ? 'Open match report' : undefined}
            >
              <div className="side home" title={home}>
                <span className="name">{home}</span>
                <TeamCrest src={homeCrest} name={home} />
              </div>
              <div
                className="score"
                role="status"
                aria-live="polite"
                aria-label={`${home} ${score.home}, ${away} ${score.away}`}
              >
                <strong>{score.home}</strong>
                <span className="separator" aria-hidden="true">
                  :
                </span>
                <strong>{score.away}</strong>
              </div>
              <div className="side away" title={away}>
                <TeamCrest src={awayCrest} name={away} />
                <span className="name">{away}</span>
              </div>
            </button>
          )}
        </div>
        {hasEvents && (
          <button
            type="button"
            className="events"
            data-open={openPath ? 'true' : 'false'}
            onClick={openMatch}
            disabled={!openPath}
            title={openPath ? 'Open match report' : undefined}
            style={{
              border: 0,
              padding: 0,
              margin: 0,
              background: 'transparent',
              color: 'inherit',
              font: 'inherit',
              cursor: openPath ? 'pointer' : 'default',
              width: '100%',
            }}
          >
            <EventsRow match={match} side="home" align="home" />
            <div className="events-gap" aria-hidden="true" />
            <EventsRow match={match} side="away" align="away" />
          </button>
        )}
      </div>
      <button
        type="button"
        className="watch"
        onClick={() =>
          isAuthenticated
            ? window.open(
                `https://discord.com/channels/${discordChannel}`,
                '_blank',
                'noopener,noreferrer'
              )
            : goToDiscordLogin()
        }
      >
        Watch on Discord{' '}
        <ExternalArrow aria-hidden="true" focusable="false" />
      </button>
    </Bar>
  );
}
